import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'

const FREQ_LABELS = {
  weekly: 'Weekly (every week)',
  biweekly: 'Every 2 weeks',
  triweekly: 'Every 3 weeks',
  fortweekly: 'Every 4 weeks',
  monthly: 'Monthly',
  bimonthly: 'Every 2 months',
  'one-time': 'One-time service',
  custom: 'Custom / as needed',
}

const SERVICE_LABELS = {
  standard: 'Standard Cleaning',
  deep: 'Deep Clean',
  airbnb: 'Airbnb Turnover',
  'move-out': 'Move-Out Cleaning',
  custom: 'Custom',
}


// One list of terms used by both the signing page and the downloadable PDF
function getTerms(order) {
  return [
    ['Payment', `${order.rate_type === 'hourly' ? 'The rate listed above is the agreed hourly rate, charged for the time spent cleaning on each visit' : 'The rate listed above is the agreed flat rate per visit'}, due upon completion of each service. Any additional services requested on the day of cleaning that fall outside the original scope will be communicated before proceeding and may be subject to additional charges.`],
    ['Photo Documentation', `Before and after photos may be taken during cleanings as proof of service and quality assurance. Any photos are stored securely and used internally to maintain our quality standards.`],
    ['Dispute Resolution', `Any service disputes must be raised within 24 hours of the cleaning with specific details. Claims made after 24 hours without prior notice cannot be honored.`],
    ['Property Access', `Client agrees to provide necessary access to the property on scheduled cleaning days. If access is not provided and the team cannot complete the service, the visit may be charged at the full rate.`],
    ['Changes to Scope', `Any changes to the agreed scope (additional rooms, tasks, or frequency) must be communicated in advance. Same-day additions outside the original scope are subject to additional charges.`],
    ['Liability', `Kitty's Cleaning Services carries general liability insurance. In the unlikely event of damage, the client must notify us within 24 hours with photos. We will work promptly to resolve any legitimate claims.`],
    ['Right to Refuse', `Kitty's Cleaning Services reserves the right to refuse or reschedule service if the property is found to be in an unsafe condition or contains hazards not previously disclosed, if the requested work changes from the agreed scope, or if a signed work order is not on file.`],
    ['Electronic Signature', `By signing electronically, the client agrees that their drawn or typed signature has the same legal effect as a handwritten signature under the California Uniform Electronic Transactions Act (Civil Code section 1633.1 and following).`],
    ['Authority to Sign', `The person signing confirms they are the owner of the property, or are authorized by the owner, to hire Kitty's Cleaning Services for this property and agree to these terms.`],
  ]
}

const fmtDate = d => new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
const agreementNo = order => String(order.token || order.id || '').replace(/-/g, '').slice(0, 8).toUpperCase()

export default function WorkOrderPublic({ token }) {
  const [order, setOrder] = useState(null)
  const [client, setClient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [name, setName] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [signed, setSigned] = useState(false)
  const [signing, setSigning] = useState(false)
  const [allowSocial, setAllowSocial] = useState(false)
  const [hasSig, setHasSig] = useState(false)
  const [drawing, setDrawing] = useState(false)
  const canvasRef = useRef(null)

  useEffect(() => {
    async function load() {
      const { data: wo, error } = await supabase
        .from('work_orders')
        .select('*')
        .eq('token', token)
        .maybeSingle()
      if (error || !wo) { setError('Work order not found or link has expired.'); setLoading(false); return }
      setOrder(wo)
      if (wo.status === 'signed') setSigned(true)
      // Fetch client separately
      const { data: cl } = await supabase
        .from('clients')
        .select('name, nickname, address, city')
        .eq('id', wo.client_id)
        .maybeSingle()
      setClient(cl || {})
      setLoading(false)
    }
    load()
  }, [token])

  useEffect(() => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d')
      ctx.strokeStyle = '#1a1a1a'
      ctx.lineWidth = 2.5
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
    }
  }, [loading, signed])

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top
    return { x: x * scaleX, y: y * scaleY }
  }

  function startDraw(e) {
    setDrawing(true)
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const { x, y } = getPos(e, canvas)
    ctx.beginPath(); ctx.moveTo(x, y)
  }
  function draw(e) {
    if (!drawing) return
    e.preventDefault()
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const { x, y } = getPos(e, canvas)
    ctx.lineTo(x, y); ctx.stroke()
    setHasSig(true)
  }
  function stopDraw() { setDrawing(false) }
  function clearSig() {
    const canvas = canvasRef.current
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
    setHasSig(false)
  }

  async function sign() {
    if (!name.trim()) { alert('Please enter your full name.'); return }
    if (!agreed) { alert('Please read and agree to the terms.'); return }
    if (!hasSig) { alert('Please draw your signature.'); return }
    setSigning(true)
    const signatureImage = canvasRef.current.toDataURL('image/png')
    // Signs through a database function so it works for clients who aren't
    // logged in, and only succeeds if the order isn't already signed.
    const { data: result, error: signErr } = await supabase.rpc('sign_work_order', {
      p_token: String(token),
      p_name: name.trim(),
      p_allow_social: allowSocial,
      p_signature: signatureImage,
    })
    if (signErr || !result) {
      setSigning(false)
      alert("Sorry, your signature didn't go through. Please check your connection and try again. If it keeps happening, contact Kitty's Cleaning Services.")
      return
    }
    // Re-read the order so the confirmation shows what was actually saved
    const { data: fresh } = await supabase.from('work_orders').select('*').eq('token', token).maybeSingle()
    if (fresh) setOrder(fresh)
    if (result === 'already_signed') alert('This work order was already signed.')
    setSigned(true)
    setSigning(false)
  }

  if (loading) return (
    <div style={ps.page}>
      <div style={{ textAlign: 'center', padding: 80, color: '#bbb', fontSize: 14 }}>Loading...</div>
    </div>
  )

  if (error) return (
    <div style={ps.page}>
      <div style={ps.card}>
        <div style={ps.logoBar}><span style={{ fontSize: 26 }}>🐈</span><span style={ps.logoText}>Kitty's Cleaning Services</span></div>
        <div style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>❌</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', marginBottom: 8 }}>{error}</div>
          <div style={{ fontSize: 13, color: '#999' }}>Please contact us at (310) 505-3195</div>
        </div>
      </div>
    </div>
  )

  function downloadPDF() {
    const signedDate = order.signed_at ? new Date(order.signed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    const terms = getTerms(order)
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Service Agreement - ${client?.name || ''}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  @page{size:letter;margin:16mm 16mm 18mm}
  body{font-family:Georgia,'Times New Roman',serif;color:#1a1a1a;font-size:11.5px;line-height:1.6;max-width:760px;margin:0 auto;padding:28px}
  .sans{font-family:Arial,Helvetica,sans-serif}
  .head{display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:12px;border-bottom:3px solid #e8547a;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .brand{font-family:Arial,Helvetica,sans-serif;font-size:20px;font-weight:700;letter-spacing:.2px}
  .contact{font-family:Arial,Helvetica,sans-serif;font-size:10px;color:#666;margin-top:3px}
  .doc{text-align:right;font-family:Arial,Helvetica,sans-serif}
  .doc-title{font-size:12px;font-weight:700;letter-spacing:.14em;color:#e8547a;text-transform:uppercase}
  .doc-meta{font-size:10px;color:#666;margin-top:3px}
  h2{font-family:Arial,Helvetica,sans-serif;font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.12em;margin:22px 0 8px;padding-bottom:4px;border-bottom:1px solid #ddd}
  table.details{width:100%;border-collapse:collapse}
  table.details td{padding:6px 0;border-bottom:1px solid #eee;vertical-align:top}
  table.details td.l{width:120px;font-family:Arial,Helvetica,sans-serif;font-size:9.5px;text-transform:uppercase;letter-spacing:.08em;color:#888;padding-top:8px}
  ol.terms{padding-left:0;list-style:none;counter-reset:t}
  ol.terms li{counter-increment:t;margin-bottom:8px;padding-left:26px;position:relative;page-break-inside:avoid}
  ol.terms li:before{content:"2." counter(t);position:absolute;left:0;font-family:Arial,Helvetica,sans-serif;font-size:10px;font-weight:700;color:#888;top:1px}
  ol.terms b{font-family:Arial,Helvetica,sans-serif;font-size:11px}
  .sig{display:grid;grid-template-columns:1.3fr 1fr;gap:28px;margin-top:14px;page-break-inside:avoid}
  .line{border-bottom:1px solid #1a1a1a;min-height:44px;display:flex;align-items:flex-end;padding-bottom:3px}
  .line img{height:52px;max-width:100%}
  .cap{font-family:Arial,Helvetica,sans-serif;font-size:9px;text-transform:uppercase;letter-spacing:.08em;color:#888;margin-top:4px}
  .val{font-size:15px}
  .stamp{font-family:Arial,Helvetica,sans-serif;display:inline-block;margin-top:14px;font-size:9.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#1d9e75;border:1.5px solid #1d9e75;border-radius:4px;padding:3px 8px}
  .foot{font-family:Arial,Helvetica,sans-serif;margin-top:28px;padding-top:8px;border-top:1px solid #ddd;font-size:9px;color:#999;display:flex;justify-content:space-between}
  @media print{body{padding:0}}
</style></head>
<body>
<div class="head">
  <div>
    <div class="brand">Kitty's Cleaning Services</div>
    <div class="contact">6427 Wilcox Ave, Bell, CA 90201 &nbsp;·&nbsp; (310) 505-3195 &nbsp;·&nbsp; info@kittyscleaningservices.com</div>
  </div>
  <div class="doc">
    <div class="doc-title">Service Agreement</div>
    <div class="doc-meta">No. ${agreementNo(order)}${order.created_at ? ' &nbsp;·&nbsp; Issued ' + fmtDate(order.created_at) : ''}</div>
  </div>
</div>

<h2>1. Client &amp; Service Details</h2>
<table class="details">
  <tr><td class="l">Client</td><td><b>${client?.name || ''}</b></td></tr>
  ${client?.address ? `<tr><td class="l">Property</td><td>${client.address}${client.city ? ', ' + client.city : ''}</td></tr>` : ''}
  <tr><td class="l">Service</td><td>${SERVICE_LABELS[order.service_type] || order.service_type}</td></tr>
  <tr><td class="l">Frequency</td><td>${FREQ_LABELS[order.frequency] || order.frequency}</td></tr>
  ${order.rate ? `<tr><td class="l">Rate</td><td><b>$${order.rate}</b> ${order.rate_type === 'hourly' ? 'per hour' : 'per visit (flat rate)'}</td></tr>` : ''}
  ${order.scope_notes ? `<tr><td class="l">Scope</td><td>${order.scope_notes}</td></tr>` : ''}
</table>

<h2>2. Terms &amp; Conditions</h2>
<ol class="terms">
  ${terms.map(([t, b]) => `<li><b>${t}.</b> ${b}</li>`).join('')}
</ol>

<h2>3. Photo Consent (Optional)</h2>
<p>${order.allow_social_media ? '&#9745; Client <b>agreed</b> to allow Kitty\'s Cleaning Services to share select before/after photos (kitchens, bathrooms, and common areas only, with personal information blurred) on social media.' : '&#9744; Client <b>did not opt in</b> to social media use. Any photos are for internal use only.'}</p>

<h2>4. Acknowledgment &amp; Signature</h2>
<p>By signing, the client confirms they have read and agree to all terms above and are entering into a service agreement with Kitty's Cleaning Services.</p>
<div class="sig">
  <div>
    <div class="line">${order.signature_image ? `<img src="${order.signature_image}" />` : ''}</div>
    <div class="cap">Signature</div>
  </div>
  <div>
    <div class="line"><span class="val">${signedDate}</span></div>
    <div class="cap">Date</div>
  </div>
  <div>
    <div class="line"><span class="val">${order.signed_name || ''}</span></div>
    <div class="cap">Printed name</div>
  </div>
</div>
<div class="stamp">&#10003; Electronically signed</div>

<div class="foot"><span>Kitty's Cleaning Services · Service Agreement No. ${agreementNo(order)}</span><span>Electronic service agreement</span></div>
<script>window.onload=()=>window.print()</script>
</body></html>`
    const win = window.open('', '_blank')
    win.document.write(html)
    win.document.close()
  }

  if (signed) return (
    <div style={ps.page}>
      <div style={ps.card}>
        <div style={ps.logoBar}><span style={{ fontSize: 26 }}>🐈</span><span style={ps.logoText}>Kitty's Cleaning Services</span></div>
        <div style={{ textAlign: 'center', padding: '40px 24px 32px' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', marginBottom: 10 }}>All signed!</div>
          <div style={{ fontSize: 14, color: '#666', lineHeight: 1.7, marginBottom: 24 }}>
            Thank you{order.signed_name ? `, ${order.signed_name}` : ''}!<br/>
            Your work order is signed and on file. We look forward to serving you!
          </div>
          <div style={{ background: '#fdf6f0', borderRadius: 12, padding: '16px 20px', textAlign: 'left', border: '1px solid #f0e4da', marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#e8547a', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Your service details</div>
            <div style={ps.detailRow}><span style={ps.detailLabel}>Service</span><span>{SERVICE_LABELS[order.service_type] || order.service_type}</span></div>
            <div style={ps.detailRow}><span style={ps.detailLabel}>Frequency</span><span>{FREQ_LABELS[order.frequency] || order.frequency}</span></div>
            {order.rate && <div style={ps.detailRow}><span style={ps.detailLabel}>Rate</span><span>${order.rate} {order.rate_type === 'hourly' ? 'per hour' : 'per visit'}</span></div>}
            <div style={ps.detailRow}><span style={ps.detailLabel}>Signed by</span><span style={{ fontWeight: 600 }}>{order.signed_name}</span></div>
            <div style={ps.detailRow}><span style={ps.detailLabel}>Signed on</span><span>{order.signed_at ? new Date(order.signed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}</span></div>
          </div>
          <button onClick={downloadPDF} style={{ width: '100%', padding: '14px', border: 'none', borderRadius: 12, background: '#e8547a', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 12 }}>
            📄 Download / Print Signed Agreement
          </button>
          <div style={{ fontSize: 12, color: '#bbb', lineHeight: 1.7 }}>
            Questions? Call or text (310) 505-3195<br/>
            <a href="mailto:info@kittyscleaningservices.com" style={{ color: '#e8547a' }}>info@kittyscleaningservices.com</a>
          </div>
        </div>
      </div>
    </div>
  )

  const canSign = agreed && name.trim()
  return (
    <div className="wo-page" style={ds.page}>
      <style>{`
        .wo-paper{max-width:816px;margin:0 auto;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.06),0 10px 40px rgba(0,0,0,.08);padding:56px 64px 44px;border-radius:2px}
        .wo-grid{display:grid;grid-template-columns:1fr 1fr;column-gap:32px}
        .wo-sig{display:grid;grid-template-columns:1.3fr 1fr;gap:28px}
        @media (max-width:640px){
          .wo-paper{padding:26px 18px 28px}
          .wo-grid,.wo-sig{grid-template-columns:1fr}
          .wo-head{flex-direction:column;align-items:flex-start !important;gap:8px}
          .wo-doc{text-align:left !important}
        }
        @media print{.wo-page{background:#fff !important;padding:0 !important}.wo-paper{box-shadow:none;padding:0}.wo-noprint{display:none !important}}
      `}</style>
      <div className="wo-paper">

        {/* Letterhead */}
        <div className="wo-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingBottom: 12, borderBottom: '3px solid ' + PINK }}>
          <div>
            <div style={{ fontSize: 21, fontWeight: 700, color: '#1a1a1a', letterSpacing: '.2px' }}>Kitty's Cleaning Services</div>
            <div style={{ fontSize: 11, color: '#777', marginTop: 3 }}>6427 Wilcox Ave, Bell, CA 90201 · (310) 505-3195 · info@kittyscleaningservices.com</div>
          </div>
          <div className="wo-doc" style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.14em', color: PINK, textTransform: 'uppercase' }}>Service Agreement</div>
            <div style={{ fontSize: 11, color: '#777', marginTop: 3 }}>No. {agreementNo(order)}{order.created_at ? ` · Issued ${fmtDate(order.created_at)}` : ''}</div>
          </div>
        </div>

        <p style={{ ...ds.body, marginTop: 18 }}>
          This agreement outlines the cleaning services Kitty's Cleaning Services will provide and the terms that apply.
          Please review it, then sign in section 4 to confirm.
        </p>

        {/* 1. Details */}
        <div style={ds.h2}>1. Client &amp; Service Details</div>
        <div className="wo-grid">
          <Field label="Client" value={<b>{client?.name}</b>} />
          {client?.address && <Field label="Property" value={`${client.address}${client.city ? ', ' + client.city : ''}`} />}
          <Field label="Service" value={SERVICE_LABELS[order.service_type] || order.service_type} />
          <Field label="Frequency" value={FREQ_LABELS[order.frequency] || order.frequency} />
          {order.rate && <Field label="Rate" value={<span><b>${order.rate}</b> {order.rate_type === 'hourly' ? 'per hour' : 'per visit (flat rate)'}</span>} />}
        </div>
        {order.scope_notes && <Field label="Scope of service" value={order.scope_notes} />}

        {/* 2. Terms */}
        <div style={ds.h2}>2. Terms &amp; Conditions</div>
        <div>
          {getTerms(order).map(([title, text], i) => (
            <div key={title} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#999', minWidth: 24, paddingTop: 2 }}>2.{i + 1}</div>
              <div style={ds.body}><b style={{ color: '#1a1a1a' }}>{title}.</b> {text}</div>
            </div>
          ))}
        </div>

        {/* 3. Photo consent */}
        <div style={ds.h2}>3. Photo Consent (Optional)</div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={allowSocial} onChange={e => setAllowSocial(e.target.checked)}
            style={{ marginTop: 3, flexShrink: 0, accentColor: PINK, width: 16, height: 16 }} />
          <div style={ds.body}>
            <b style={{ color: '#1a1a1a' }}>I allow Kitty's Cleaning Services to share select before/after photos on social media.</b>{' '}
            We never photograph bedrooms, always blur personal or identifying information, and only share kitchens,
            bathrooms, and common areas. Completely optional.
          </div>
        </label>

        {/* 4. Signature */}
        <div style={ds.h2}>4. Acknowledgment &amp; Signature</div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '12px 14px', border: '1px solid ' + (agreed ? PINK : '#ddd'), borderRadius: 4, background: agreed ? '#fff7fa' : '#fff', marginBottom: 22 }}>
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
            style={{ marginTop: 3, flexShrink: 0, accentColor: PINK, width: 16, height: 16 }} />
          <div style={{ ...ds.body, color: '#1a1a1a' }}>
            I have read and agree to all terms and conditions above, and I understand that by signing I am entering into a service agreement with Kitty's Cleaning Services.
          </div>
        </label>

        <div className="wo-sig">
          <div>
            <div style={{ position: 'relative', borderBottom: '1.5px solid #1a1a1a', background: '#fcfcfc' }}>
              <span style={{ position: 'absolute', left: 6, bottom: 6, fontSize: 18, color: '#bbb', pointerEvents: 'none' }}>×</span>
              <canvas ref={canvasRef} width={520} height={130}
                style={{ width: '100%', height: 110, display: 'block', cursor: 'crosshair', touchAction: 'none' }}
                onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={ds.cap}>Signature *{!hasSig && <span style={{ textTransform: 'none', letterSpacing: 0, color: '#bbb' }}> (draw with your finger or mouse)</span>}</div>
              <button className="wo-noprint" onClick={clearSig} style={{ fontSize: 11, color: '#999', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 4 }}>Clear</button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 18 }}>
            <div>
              <input style={ds.lineInput} placeholder="Type your full legal name" value={name} onChange={e => setName(e.target.value)} />
              <div style={ds.cap}>Printed name *</div>
            </div>
            <div>
              <div style={{ ...ds.lineInput, color: '#1a1a1a' }}>{fmtDate(new Date())}</div>
              <div style={ds.cap}>Date</div>
            </div>
          </div>
        </div>

        <div className="wo-noprint" style={{ textAlign: 'center', marginTop: 30 }}>
          <button
            style={{ width: '100%', maxWidth: 340, padding: '14px 18px', border: 'none', borderRadius: 6,
              background: canSign ? PINK : '#eee', color: canSign ? '#fff' : '#aaa', fontSize: 15, fontWeight: 700,
              cursor: canSign ? 'pointer' : 'not-allowed', transition: 'background .2s' }}
            onClick={sign} disabled={signing || !canSign}>
            {signing ? 'Signing...' : 'Sign Agreement'}
          </button>
          <div style={{ fontSize: 11, color: '#999', marginTop: 10, lineHeight: 1.7 }}>
            Questions? Call or text <a href="tel:+13105053195" style={{ color: PINK }}>(310) 505-3195</a>
          </div>
        </div>

        <div style={{ marginTop: 28, paddingTop: 8, borderTop: '1px solid #e5e5e5', fontSize: 10, color: '#aaa', display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <span>Kitty's Cleaning Services · Service Agreement No. {agreementNo(order)}</span>
          <span>Electronic service agreement</span>
        </div>
      </div>
    </div>
  )
}

const PINK = '#e8547a'
const ps = {
  page: { minHeight: '100vh', background: '#f7f3f0', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '20px 12px 40px', fontFamily: 'Arial, sans-serif' },
  card: { background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', width: '100%', maxWidth: 540, overflow: 'hidden' },
  logoBar: { background: PINK, padding: '16px 22px', display: 'flex', alignItems: 'center', gap: 10 },
  logoText: { fontSize: 17, fontWeight: 700, color: '#fff', letterSpacing: '.3px' },
  detailRow: { display: 'flex', gap: 12, marginBottom: 8, fontSize: 13, alignItems: 'flex-start' },
  detailLabel: { color: '#999', minWidth: 75, flexShrink: 0, fontSize: 12 },
  inputLabel: { fontSize: 11, color: '#999', marginBottom: 5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' },
  input: { width: '100%', padding: '11px 14px', border: '1.5px solid #f0e4da', borderRadius: 9, fontSize: 14, background: '#fdf6f0', color: '#1a1a1a', fontFamily: 'Arial, sans-serif', boxSizing: 'border-box' },
}

function Field({ label, value }) {
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: '#999', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13.5, color: '#1a1a1a', lineHeight: 1.5 }}>{value}</div>
    </div>
  )
}

const ds = {
  page: { minHeight: '100vh', background: '#ebe8e4', padding: '32px 12px 48px', fontFamily: 'Arial, Helvetica, sans-serif' },
  h2: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.12em', color: '#1a1a1a', margin: '26px 0 10px', paddingBottom: 5, borderBottom: '1px solid #ddd' },
  body: { fontSize: 13, color: '#444', lineHeight: 1.65 },
  cap: { fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', color: '#888', marginTop: 5 },
  lineInput: { width: '100%', border: 'none', borderBottom: '1.5px solid #1a1a1a', padding: '8px 2px 6px', fontSize: 16, fontFamily: 'Georgia, "Times New Roman", serif', background: 'transparent', outline: 'none', boxSizing: 'border-box', borderRadius: 0 },
}
